import { message } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import "moment/locale/es";
import ModalItem from "./ModalItem";
import TableItem from "./TableItem";
import cuentaMask from "./cuentaMask";
import dineroDisabledMask from "./dineroDisabledMask";
import AccionesPolizaReporte from "./AccionesPolizaReporte";
import ModalPdfPoliza from "./ModalPdfPoliza";
import ModalEditarPoliza from "./ModalEditarPoliza";

/*
  Detalle de los movimientos de una cuenta contable en un periodo.
  Lo abre la Balanza de Comprobacion: sus renglones son cuentas, no polizas, asi que
  este modal es el paso intermedio para llegar a la poliza de cada movimiento.

  Si la cuenta clicada es de nivel 1-4 el backend trae los movimientos de toda la rama,
  que es lo que cuadra con el saldo acumulado que muestra la balanza en ese renglon.

  El Estado de Resultados por Periodo lo reusa celda por celda, y por eso hay dos
  props opcionales:
   - `periodo`: el rango del bucket de esa columna (un mes, una semana...), que
     sustituye al periodo completo del reporte.
   - `subtitulo`: la etiqueta de la columna, para que el titulo diga que celda se
     abrio y no solo que cuenta.
  Y `filtros.cuentasExcluidas`, que viaja al backend para que el detalle no liste
  movimientos de ramas que la celda no incluye. Sin ninguna de las tres, el modal
  se comporta exactamente como antes.
*/
class ModalMovimientosCuenta extends Component {
  state = {
    loading: false,
    movimientos: [],
    modalPdfPoliza: false,
    polizaIdPdf: null,
    modalEditarPoliza: false,
    polizaIdEditar: null,
  };

  componentDidUpdate(prevProps) {
    // Tambien se reconsulta si cambio la celda estando el modal abierto: el
    // Estado de Resultados por Periodo puede cambiar `cuenta` o `periodo` sin
    // cerrar el modal.
    const cambioCelda =
      this.props.visible &&
      (prevProps.cuenta !== this.props.cuenta || prevProps.periodo !== this.props.periodo);

    if (this.props.visible !== prevProps.visible) {
      if (this.props.visible && this.props.cuenta) {
        this.consultarMovimientos();
      } else if (!this.props.visible) {
        this.setState({ movimientos: [] });
      }
    } else if (cambioCelda && this.props.cuenta) {
      this.consultarMovimientos();
    }
  }

  consultarMovimientos() {
    const { cuenta, filtros } = this.props;
    if (!cuenta || !filtros) return;
    this.setState({ loading: true });
    axios
      .post(
        "reportesFinancieros/movimientosDeCuenta",
        {
          empresaId: this.props.empresaId,
          cuentaContableId: cuenta._id,
          periodo: this.props.periodo || filtros.periodo,
          filtrarProyecto: filtros.filtrarProyecto,
          proyectos: filtros.proyectos,
          unidadDeNegocioId: filtros.unidadDeNegocioId,
          cuentasExcluidas: filtros.cuentasExcluidas,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ loading: false, movimientos: res.data.movimientos });
        } else {
          message.error("Ha sucedido un error y no se cargaron los movimientos");
          this.setState({ loading: false, movimientos: [] });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargaron los movimientos");
        this.setState({ loading: false, movimientos: [] });
      });
  }

  abrirVerPoliza = (polizaId) => {
    this.setState({ modalPdfPoliza: true, polizaIdPdf: polizaId });
  };
  cerrarVerPoliza = () => {
    this.setState({ modalPdfPoliza: false, polizaIdPdf: null });
  };
  abrirEditarPoliza = (polizaId) => {
    this.setState({ modalEditarPoliza: true, polizaIdEditar: polizaId });
  };
  cerrarEditarPoliza = () => {
    this.setState({ modalEditarPoliza: false, polizaIdEditar: null });
  };
  polizaEditada = () => {
    this.consultarMovimientos();
    if (this.props.onPolizaEditada) this.props.onPolizaEditada();
  };

  render() {
    const { cuenta } = this.props;

    const columns = [
      {
        title: "Fecha",
        dataIndex: "fechaRegistro",
        key: "fechaRegistro",
        render: (value) => (value ? moment(value).locale("es").format("L") : ""),
        width: "10%",
      },
      {
        title: "Cuenta",
        dataIndex: "cuenta",
        key: "cuenta",
        render: (value) => cuentaMask(value),
        buscar: "cuenta",
        width: "13%",
      },
      {
        title: "Concepto",
        dataIndex: "concepto",
        key: "concepto",
        buscar: "concepto",
        width: "26%",
      },
      {
        title: "Poliza",
        dataIndex: "numPoliza",
        key: "numPoliza",
        buscar: "numPoliza",
        width: "23%",
      },
      {
        title: "Cargo",
        dataIndex: "cargo",
        key: "cargo",
        render: (value, record) => (record.cargo ? dineroDisabledMask(record.monto) : "-"),
        align: "right",
        width: "10%",
      },
      {
        title: "Abono",
        dataIndex: "abono",
        key: "abono",
        render: (value, record) => (!record.cargo ? dineroDisabledMask(record.monto) : "-"),
        align: "right",
        width: "10%",
      },
      {
        title: "Acción",
        dataIndex: "accion",
        key: "accion",
        align: "center",
        width: "8%",
        render: (value, record) => (
          <AccionesPolizaReporte
            polizaId={record.polizaId}
            polizaManual={record.polizaManual}
            polizaEstado={record.polizaEstado}
            permisoEditarPolizas={this.props.permisoEditarPolizas}
            editarTodasLasPolizas={this.props.editarTodasLasPolizas}
            onVer={this.abrirVerPoliza}
            onEditar={this.abrirEditarPoliza}
          />
        ),
      },
    ];

    // El renglon de la balanza ya trae la cuenta enmascarada en `cuenta`
    const codigoCuenta = cuenta
      ? cuenta.cuenta ||
        cuentaMask(
          (cuenta.nivel1 || "") +
            (cuenta.nivel2 || "") +
            (cuenta.nivel3 || "") +
            (cuenta.nivel4 || "") +
            (cuenta.nivel5 || "")
        )
      : "";
    const titulo = cuenta
      ? "Movimientos de " +
        codigoCuenta +
        " - " +
        (cuenta.descripcion || "") +
        (this.props.subtitulo ? " · " + this.props.subtitulo : "")
      : "Movimientos de la Cuenta";

    return (
      <>
        <ModalItem
          title={titulo}
          visible={this.props.visible}
          spinning={this.state.loading}
          onCancel={this.props.onCancel}
          paddingTop
          width={1200}
          footer={null}
        >
          <TableItem
            columns={columns}
            dataSource={this.state.movimientos}
            size="small"
            pagination={false}
            rowKey={(value) => value._id}
            onRow={(record) => ({
              onDoubleClick: () => {
                if (record.polizaId) this.abrirVerPoliza(record.polizaId);
              },
            })}
          />
        </ModalItem>
        <ModalPdfPoliza
          visible={this.state.modalPdfPoliza}
          polizaId={this.state.polizaIdPdf}
          onCancel={this.cerrarVerPoliza}
        />
        <ModalEditarPoliza
          visible={this.state.modalEditarPoliza}
          polizaId={this.state.polizaIdEditar}
          onCancel={this.cerrarEditarPoliza}
          onGuardado={this.polizaEditada}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ModalMovimientosCuenta);
